/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-06-25
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Pied de page du site UK.
 *
 * Quatre colonnes — marque, entreprise, services, contact — puis une barre de
 * bas de page portant les droits a gauche et les liens juridiques a droite. Le
 * pied de page du Tchad reprend la meme structure avec ses propres
 * informations, pour que le passage d'un site a l'autre ne desoriente pas.
 *
 * La liste des services est tiree des traductions plutot que codee en dur :
 * elle suit ainsi automatiquement le catalogue affiche dans la navigation.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import Wordmark, { lockupHeightEm } from "./Wordmark";
import { buildLegal } from "../lib/legal";
import { Mail, Phone, MapPin } from "lucide-react";

const Footer = () => {
  const { t, lang } = useLang();
  if (!t) return null;
  const services = t.services?.items || [];

  return (
    <footer
      data-testid="site-footer"
      className="bg-slate-950 text-slate-300 mt-24"
    >
      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-1">
            {/* La taille suit la colonne de la grille, qui se resserre a
                150 px au passage en quatre colonnes : a `text-[3rem]` fixe, le
                logotype debordait de sa case et chevauchait « Entreprise ». */}
            <div className="mb-4 flex items-center gap-3 text-[2.25rem] md:text-[2.5rem] lg:text-[3rem]">
              <img
                src="/images/logo-prenim-uk.webp"
                alt=""
                aria-hidden="true"
                width="40"
                height="45"
                style={{ height: `${lockupHeightEm(0.22)}em` }}
                className="w-auto shrink-0 object-contain"
              />
              <Wordmark
                name="Prenim"
                tagline="Digital Transformation"
                ratio={0.22}
                testId="footer-wordmark"
                nameClassName="text-white"
                taglineClassName="text-slate-400"
              />
            </div>
            <p className="text-sm leading-relaxed text-slate-400 max-w-xs">
              {t.footer.tagline}
            </p>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              {t.footer.columns.company}
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link className="hover:text-cyan-400 transition-colors" to="/about" data-testid="footer-about">
                  {t.navigation.about}
                </Link>
              </li>
              <li>
                <Link className="hover:text-cyan-400 transition-colors" to="/projects" data-testid="footer-projects">
                  {t.navigation.projects}
                </Link>
              </li>
              <li>
                <Link className="hover:text-cyan-400 transition-colors" to="/careers" data-testid="footer-careers">
                  {t.navigation.careers}
                </Link>
              </li>
              <li>
                <Link className="hover:text-cyan-400 transition-colors" to="/contact" data-testid="footer-contact">
                  {t.navigation.contact}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              {t.footer.columns.services}
            </h3>
            <ul className="space-y-2 text-sm">
              {services.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`footer-service-${s.slug}`}
                    className="hover:text-cyan-400 transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              {t.footer.columns.contact}
            </h3>
            <div className="space-y-4 text-sm">
              <div>
                <p className="text-white font-semibold mb-1">United Kingdom</p>
                <p className="flex items-start gap-2 text-slate-400">
                  <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-cyan-400" />
                  <span>30 Button Hill, Leeds, LS7 3DA</span>
                </p>
                <p className="flex items-center gap-2 text-slate-400 mt-1">
                  <Phone className="h-4 w-4 shrink-0 text-cyan-400" />
                  <a href="tel:+441138805181" className="hover:text-cyan-400">+44 113 880 5181</a>
                </p>
              </div>
              <div>
                <p className="text-white font-semibold mb-1">Tchad</p>
                <p className="flex items-start gap-2 text-slate-400">
                  <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-cyan-400" />
                  <span>694 Avenue de Bezo, N'Djamena</span>
                </p>
                <p className="flex items-center gap-2 text-slate-400 mt-1">
                  <Phone className="h-4 w-4 shrink-0 text-cyan-400" />
                  <a href="tel:+23585206761" className="hover:text-cyan-400">+235 85 20 67 61</a>
                </p>
                <p className="flex items-center gap-2 text-slate-400 mt-1">
                  <Phone className="h-4 w-4 shrink-0 text-cyan-400" />
                  <a href="tel:+23590176613" className="hover:text-cyan-400">+235 90 17 66 13</a>
                </p>
              </div>
              <p className="flex items-center gap-2 text-slate-400">
                <Mail className="h-4 w-4 shrink-0 text-cyan-400" />
                <a href="mailto:hello@prenim.co.uk" className="hover:text-cyan-400">hello@prenim.co.uk</a>
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <p>{t.footer.copyright}</p>
          {/* Ces deux libelles etaient des <span> : ils avaient l'apparence
              d'un lien, le curseur d'un lien, et ne menaient nulle part. */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link
              to="/privacy"
              data-testid="footer-privacy"
              className="transition-colors hover:text-cyan-400"
            >
              {buildLegal("privacy", "uk", lang).shortTitle}
            </Link>
            <Link
              to="/terms"
              data-testid="footer-terms"
              className="transition-colors hover:text-cyan-400"
            >
              {buildLegal("terms", "uk", lang).shortTitle}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
